/**
 * Items — pure, framework-agnostic item definitions and use-logic.
 *
 * Caller (BattleEngine / Overworld controller) is responsible for actually
 * mutating party state. These functions describe *what* should happen via
 * a discriminated UseResult, plus log lines for the battle UI.
 */

export type ItemCategory = 'ball' | 'heal' | 'flee' | 'misc';

export type ItemEffect = {
  catchBonus?: number;
  healAmount?: number;
  healPercent?: number;
  revives?: boolean;
  teachesMoveId?: string;
  levelUp?: boolean;
  escapes?: boolean;
};

export type Item = {
  id: string;
  name: string;
  description: string;
  category: ItemCategory;
  usableInBattle: boolean;
  usableInOverworld: boolean;
  effect?: ItemEffect;
};

export type UseResult =
  | { kind: 'healed'; targetIndex: number; newHp: number; logLine: string }
  | { kind: 'revived'; targetIndex: number; newHp: number; logLine: string }
  | { kind: 'caught'; success: boolean; logLine: string }
  | { kind: 'fled'; logLine: string }
  | { kind: 'taught'; targetIndex: number; moveId: string; logLine: string }
  | { kind: 'leveled'; targetIndex: number; newLevel: number; logLine: string }
  | { kind: 'fail'; logLine: string };

// ---------------------------------------------------------------------------
// Item registry
// ---------------------------------------------------------------------------

export const ITEMS: Record<string, Item> = {
  catch_ball: {
    id: 'catch_ball',
    name: 'Catch Ball',
    description: 'A basic ball used to catch wild Surggies.',
    category: 'ball',
    usableInBattle: true,
    usableInOverworld: false,
    effect: { catchBonus: 1.0 },
  },
  great_ball: {
    id: 'great_ball',
    name: 'Great Ball',
    description: 'A better ball with a higher catch rate.',
    category: 'ball',
    usableInBattle: true,
    usableInOverworld: false,
    effect: { catchBonus: 1.5 },
  },
  premier_ball: {
    id: 'premier_ball',
    name: 'Premier Ball',
    description: 'A rare ball with a much higher catch rate.',
    category: 'ball',
    usableInBattle: true,
    usableInOverworld: false,
    effect: { catchBonus: 2.5 },
  },
  potion: {
    id: 'potion',
    name: 'Potion',
    description: 'Restores 25 HP to one Surggie.',
    category: 'heal',
    usableInBattle: true,
    usableInOverworld: true,
    effect: { healAmount: 25 },
  },
  great_potion: {
    id: 'great_potion',
    name: 'Great Potion',
    description: 'Restores 70 HP to one Surggie.',
    category: 'heal',
    usableInBattle: true,
    usableInOverworld: true,
    effect: { healAmount: 70 },
  },
  max_potion: {
    id: 'max_potion',
    name: 'Max Potion',
    description: 'Fully restores HP of one Surggie.',
    category: 'heal',
    usableInBattle: true,
    usableInOverworld: true,
    effect: { healPercent: 1.0 },
  },
  revive: {
    id: 'revive',
    name: 'Revive',
    description: 'Revives a fainted Surggie to half HP.',
    category: 'heal',
    usableInBattle: true,
    usableInOverworld: true,
    effect: { revives: true, healPercent: 0.5 },
  },
  max_revive: {
    id: 'max_revive',
    name: 'Max Revive',
    description: 'Revives a fainted Surggie to full HP.',
    category: 'heal',
    usableInBattle: true,
    usableInOverworld: true,
    effect: { revives: true, healPercent: 1.0 },
  },
  escape_orb: {
    id: 'escape_orb',
    name: 'Escape Orb',
    description: 'Guarantees escape from a wild battle.',
    category: 'flee',
    usableInBattle: true,
    usableInOverworld: false,
    effect: { escapes: true },
  },
  rare_candy: {
    id: 'rare_candy',
    name: 'Rare Candy',
    description: 'Raises a Surggie\u2019s level by 1.',
    category: 'misc',
    usableInBattle: false,
    usableInOverworld: true,
    effect: { levelUp: true },
  },
  tm_pitch_slam: {
    id: 'tm_pitch_slam',
    name: 'TM Pitch Slam',
    description: 'Teaches a Surggie the move Pitch Slam.',
    category: 'misc',
    usableInBattle: false,
    usableInOverworld: true,
    // Move id "2" matches MOVES_DATA entry for Pitch Slam.
    effect: { teachesMoveId: '2' },
  },
  repel: {
    id: 'repel',
    name: 'Repel',
    description: 'Temporarily reduces wild encounter rate.',
    category: 'misc',
    usableInBattle: false,
    usableInOverworld: true,
    // Partial impl: the overworld controller is expected to read this flag
    // and apply its own encounter-rate modifier for a duration of its choosing.
    effect: {},
  },
};

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function getItem(id: string): Item | null {
  return ITEMS[id] ?? null;
}

export function getItemsByCategory(cat: ItemCategory): Item[] {
  return Object.values(ITEMS).filter((it) => it.category === cat);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type TargetParticipant = {
  hpCurrent: number;
  hpMax: number;
  fainted?: boolean;
  level?: number;
};

type OpponentParticipant = {
  hpCurrent: number;
  hpMax: number;
};

function computeHealAmount(
  item: Item,
  hpCurrent: number,
  hpMax: number,
): number {
  const effect = item.effect;
  if (!effect) return 0;
  const missing = Math.max(0, hpMax - hpCurrent);
  if (typeof effect.healPercent === 'number') {
    const target = Math.floor(hpMax * effect.healPercent);
    return Math.min(missing, target);
  }
  if (typeof effect.healAmount === 'number') {
    return Math.min(missing, effect.healAmount);
  }
  return 0;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function rollCatch(
  catchBonus: number,
  opponent: OpponentParticipant,
): boolean {
  const ratio = opponent.hpMax > 0 ? opponent.hpCurrent / opponent.hpMax : 1;
  const base = (1 - ratio) * 0.5 + 0.2;
  const chance = clamp(base * catchBonus, 0, 0.95);
  return Math.random() < chance;
}

// ---------------------------------------------------------------------------
// Use in battle
// ---------------------------------------------------------------------------

export function useItemInBattle(
  itemId: string,
  opts: {
    targetIndex: number;
    target?: TargetParticipant;
    opponent?: OpponentParticipant;
    isWild: boolean;
  },
): UseResult {
  const item = getItem(itemId);
  if (!item) {
    return { kind: 'fail', logLine: 'That item does not exist.' };
  }
  if (!item.usableInBattle) {
    return { kind: 'fail', logLine: `${item.name} can\u2019t be used in battle.` };
  }

  const { targetIndex, target, opponent, isWild } = opts;

  switch (item.category) {
    case 'ball': {
      if (!isWild) {
        return {
          kind: 'fail',
          logLine: 'You can\u2019t catch a trainer\u2019s Surggie!',
        };
      }
      if (!opponent) {
        return { kind: 'fail', logLine: 'There is no target to catch.' };
      }
      const bonus = item.effect?.catchBonus ?? 1;
      const success = rollCatch(bonus, opponent);
      if (success) {
        return {
          kind: 'caught',
          success: true,
          logLine: 'Gotcha! The Surggie was caught!',
        };
      }
      return {
        kind: 'caught',
        success: false,
        logLine: 'Oh no! It broke free!',
      };
    }

    case 'flee': {
      if (!item.effect?.escapes) {
        return { kind: 'fail', logLine: 'It had no effect...' };
      }
      if (!isWild) {
        return {
          kind: 'fail',
          logLine: 'You can\u2019t flee from a trainer battle!',
        };
      }
      return { kind: 'fled', logLine: 'You smoothly escaped the battle!' };
    }

    case 'heal': {
      if (!target) {
        return { kind: 'fail', logLine: 'There is no Surggie to heal.' };
      }
      const revives = item.effect?.revives === true;

      if (revives) {
        if (!target.fainted) {
          return {
            kind: 'fail',
            logLine: 'It had no effect on a healthy Surggie...',
          };
        }
        const healed = computeHealAmount(item, 0, target.hpMax);
        const newHp = clamp(healed, 1, target.hpMax);
        return {
          kind: 'revived',
          targetIndex,
          newHp,
          logLine: 'Your Surggie was revived and is ready to battle!',
        };
      }

      if (target.fainted || target.hpCurrent <= 0) {
        return {
          kind: 'fail',
          logLine: 'A fainted Surggie can\u2019t be healed by that.',
        };
      }
      if (target.hpCurrent >= target.hpMax) {
        return {
          kind: 'fail',
          logLine: 'HP is already full!',
        };
      }
      const heal = computeHealAmount(item, target.hpCurrent, target.hpMax);
      if (heal <= 0) {
        return { kind: 'fail', logLine: 'It had no effect...' };
      }
      const newHp = clamp(target.hpCurrent + heal, 0, target.hpMax);
      return {
        kind: 'healed',
        targetIndex,
        newHp,
        logLine: `Your Surggie\u2019s HP was restored by ${heal}!`,
      };
    }

    case 'misc':
    default:
      return {
        kind: 'fail',
        logLine: `${item.name} can\u2019t be used right now.`,
      };
  }
}

// ---------------------------------------------------------------------------
// Use in overworld
// ---------------------------------------------------------------------------

export function useItemOverworld(
  itemId: string,
  opts: {
    targetIndex: number;
    target?: TargetParticipant;
  },
): UseResult {
  const item = getItem(itemId);
  if (!item) {
    return { kind: 'fail', logLine: 'That item does not exist.' };
  }
  if (!item.usableInOverworld) {
    return {
      kind: 'fail',
      logLine: `${item.name} can only be used in battle.`,
    };
  }

  const { targetIndex, target } = opts;

  switch (item.category) {
    case 'heal': {
      if (!target) {
        return { kind: 'fail', logLine: 'There is no Surggie to use it on.' };
      }
      const revives = item.effect?.revives === true;

      if (revives) {
        if (!target.fainted) {
          return {
            kind: 'fail',
            logLine: 'It had no effect on a healthy Surggie...',
          };
        }
        const healed = computeHealAmount(item, 0, target.hpMax);
        const newHp = clamp(healed, 1, target.hpMax);
        return {
          kind: 'revived',
          targetIndex,
          newHp,
          logLine: 'Your Surggie was revived!',
        };
      }

      if (target.fainted || target.hpCurrent <= 0) {
        return {
          kind: 'fail',
          logLine: 'A fainted Surggie needs a Revive first.',
        };
      }
      if (target.hpCurrent >= target.hpMax) {
        return { kind: 'fail', logLine: 'HP is already full!' };
      }
      const heal = computeHealAmount(item, target.hpCurrent, target.hpMax);
      if (heal <= 0) {
        return { kind: 'fail', logLine: 'It had no effect...' };
      }
      const newHp = clamp(target.hpCurrent + heal, 0, target.hpMax);
      return {
        kind: 'healed',
        targetIndex,
        newHp,
        logLine: `Your Surggie\u2019s HP was restored by ${heal}!`,
      };
    }

    case 'misc': {
      if (item.effect?.levelUp) {
        if (!target) {
          return {
            kind: 'fail',
            logLine: 'There is no Surggie to use it on.',
          };
        }
        const currentLevel = target.level ?? 1;
        const newLevel = clamp(currentLevel + 1, 1, 100);
        if (newLevel === currentLevel) {
          return {
            kind: 'fail',
            logLine: 'Your Surggie is already at the max level!',
          };
        }
        return {
          kind: 'leveled',
          targetIndex,
          newLevel,
          logLine: `Your Surggie grew to level ${newLevel}!`,
        };
      }
      if (item.effect?.teachesMoveId) {
        if (!target) {
          return {
            kind: 'fail',
            logLine: 'There is no Surggie to teach.',
          };
        }
        const moveId = item.effect.teachesMoveId;
        return {
          kind: 'taught',
          targetIndex,
          moveId,
          logLine: `Your Surggie learned a new move!`,
        };
      }
      // Repel and other partial-impl misc items.
      return {
        kind: 'fail',
        logLine: `${item.name} was used. (Effect handled by overworld.)`,
      };
    }

    case 'ball':
      return {
        kind: 'fail',
        logLine: 'Balls can only be thrown in battle.',
      };

    case 'flee':
    default:
      return {
        kind: 'fail',
        logLine: `${item.name} can\u2019t be used out here.`,
      };
  }
}
